import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Music, Building2, Trophy, Sparkles, Calendar, MapPin } from "lucide-react";

/**
 * EventsTicker — infinite "news" marquee of upcoming city events.
 *
 *  • Calls /api/events/by-city?city=... ; returns null when empty.
 *  • Seamless JS-driven auto-scroll (slow). Two identical copies of the
 *    sequence → wraps invisibly for an infinite loop.
 *  • Fully swipeable with the finger on mobile (native momentum scroll):
 *    auto-scroll pauses while touching / hovering and resumes shortly after.
 *  • Edges fade to transparent (mask).
 */
const SPEED = 42; // px/sec — slow, news-ticker pace

const TYPE_META = {
  concerto: { icon: Music, label: "Concerto", color: "#A855F7", bg: "#FAF5FF" },
  festival: { icon: Sparkles, label: "Festival", color: "#F59E0B", bg: "#FFFBEB" },
  fiera: { icon: Building2, label: "Fiera", color: "#3B82F6", bg: "#EFF6FF" },
  sportivo: { icon: Trophy, label: "Sport", color: "#10B981", bg: "#ECFDF5" },
  evento: { icon: Calendar, label: "Evento", color: "#71717A", bg: "#F4F4F5" },
};

const fmtDate = (start, end) => {
  if (!start) return "";
  const toIt = (iso) => {
    try {
      const d = new Date(`${iso}T00:00:00`);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    } catch { return iso; }
  };
  if (end && end !== start) return `${toIt(start)} → ${toIt(end)}`;
  return toIt(start);
};

const EventChip = ({ e }) => {
  const meta = TYPE_META[e.type] || TYPE_META.evento;
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-2.5 shrink-0 pr-3 pl-1">
      <span
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: meta.bg, color: meta.color }}
      >
        <Icon className="w-4 h-4" strokeWidth={2.3} />
      </span>
      <div className="min-w-0 flex flex-col leading-tight">
        <span className="flex items-center gap-1.5 max-w-[240px]">
          <span
            className="text-[8px] font-black uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full shrink-0"
            style={{ background: `${meta.color}18`, color: meta.color }}
          >
            {meta.label}
          </span>
          <span className="text-[13px] font-black text-zinc-900 truncate">{e.name}</span>
        </span>
        <span className="text-[10.5px] text-zinc-500 font-semibold mt-0.5 inline-flex items-center gap-2 truncate">
          <span className="inline-flex items-center gap-1"><Calendar className="w-2.5 h-2.5" strokeWidth={2.6} />{fmtDate(e.start, e.end)}</span>
          {e.venue && <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-2.5 h-2.5" strokeWidth={2.6} /><span className="truncate max-w-[130px]">{e.venue}</span></span>}
        </span>
      </div>
      <span className="w-1 h-1 rounded-full bg-zinc-300 mx-1 shrink-0" />
    </div>
  );
};

export default function EventsTicker({ apiBaseUrl, city, propertyId = "", theme, flush = false, className = "" }) {
  const [items, setItems] = useState(null); // null = loading; [] = none; [...] = render
  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimer = useRef(null);

  useEffect(() => {
    if (!city) { setItems([]); return; }
    let abort = false;
    (async () => {
      try {
        const r = await axios.get(`${apiBaseUrl}/api/events/by-city`, { params: { city, property_id: propertyId, limit: 30 } });
        if (!abort) setItems(r.data?.items || []);
      } catch {
        if (!abort) setItems([]);
      }
    })();
    return () => { abort = true; };
  }, [apiBaseUrl, city, propertyId]);

  const base = items || [];

  // One sequence, repeated enough to always overflow, rendered twice for loop.
  const seq = useMemo(() => {
    if (base.length === 0) return [];
    const reps = Math.max(2, Math.ceil(10 / base.length));
    return Array.from({ length: reps }).flatMap(() => base);
  }, [base]);

  // JS-driven seamless auto-scroll + infinite wrap.
  // Uses a float accumulator (not el.scrollLeft directly) because iOS Safari
  // rounds scrollLeft to integers, so sub-pixel increments (<1px/frame) were
  // lost and the ticker stayed frozen on mobile.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || seq.length === 0) return;
    let raf, last = null;
    let pos = el.scrollLeft || 0;
    const step = (ts) => {
      if (last == null) last = ts;
      const dt = Math.min(0.05, (ts - last) / 1000); // clamp on tab refocus
      last = ts;
      const half = el.scrollWidth / 2;
      if (pausedRef.current) {
        // User is dragging: follow the real scroll position and wrap it.
        pos = el.scrollLeft;
        if (half > 0) {
          if (pos >= half) { pos -= half; el.scrollLeft = pos; }
          else if (pos < 0) { pos += half; el.scrollLeft = pos; }
        }
      } else {
        pos += SPEED * dt;
        if (half > 0 && pos >= half) pos -= half;
        el.scrollLeft = pos;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [seq.length]);

  const pause = () => { pausedRef.current = true; if (resumeTimer.current) { clearTimeout(resumeTimer.current); resumeTimer.current = null; } };
  const resume = (delay = 0) => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => { pausedRef.current = false; }, delay);
  };

  if (items !== null && base.length === 0) return null;

  const border = theme?.border || "rgba(0,0,0,0.06)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5 }}
      className={`${flush ? "" : "mt-5 md:mt-6"} ${className} rounded-3xl relative overflow-hidden`}
      style={{ background: "rgba(255,255,255,0.55)", border: `1px solid ${border}`, backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
      data-testid="events-ticker"
    >
      {/* Marquee — swipeable + auto-scroll */}
      <div className="et-mask py-4">
        <div
          ref={scrollRef}
          className="et-scroll w-full"
          data-testid="events-marquee"
          onMouseEnter={pause}
          onMouseLeave={() => resume(250)}
          onTouchStart={pause}
          onTouchEnd={() => resume(1600)}
          onPointerDown={pause}
          onPointerUp={() => resume(1600)}
        >
          <div className="flex w-max">
            {[...seq, ...seq].map((e, i) => (
              <EventChip key={`${e.name}-${i}`} e={e} />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .et-mask { overflow: hidden; -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 7%, #000 93%, transparent 100%); mask-image: linear-gradient(90deg, transparent 0, #000 7%, #000 93%, transparent 100%); }
        .et-scroll { overflow-x: auto; overflow-y: hidden; touch-action: pan-x; overscroll-behavior-x: contain; -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none; cursor: grab; }
        .et-scroll::-webkit-scrollbar { display: none; height: 0; }
        .et-scroll:active { cursor: grabbing; }
      `}</style>
    </motion.div>
  );
}
